import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Pill } from "@/components/ui/pill";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";
import { formatQuantity } from "@/lib/quantity-format";
import { closingColumns } from "./lib/closing-columns";
import { formatBucketLabel } from "./lib/bucket-label";
import type { ClosingGridMode, ReportGroupBy, ReportStage } from "@/types/production-report.types";

// Spec 083 (T036) — o FECHAMENTO.
//
// É a tela que substitui a planilha, então ela precisa aguentar a conferência número a número: o
// modo completo abre uma coluna por produto e por perda, como a planilha da fábrica. O resumido é o
// padrão porque a maioria das visitas é de relance.
//
// ⚠️ A linha de TOTAL não é somada aqui. Ela vem da etapa, que o servidor construiu dobrando os
// mesmos agrupamentos — somar de novo no cliente criaria uma segunda verdade que divergiria no
// primeiro arredondamento.

interface Props {
  stage: ReportStage;
  groupBy: ReportGroupBy;
  mode: ClosingGridMode;
  onModeChange: (mode: ClosingGridMode) => void;
  heroId: string | null;
}

const pct = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 1 });

/** Quantas linhas a grade tem, no substantivo do agrupamento — o total da grade não é o do filtro. */
const PERIODO: Record<ReportGroupBy, [string, string]> = {
  day: ["dia", "dias"],
  week: ["semana", "semanas"],
  month: ["mês", "meses"],
};

function contagem(stage: ReportStage, groupBy: ReportGroupBy): string {
  const n = stage.buckets.length;
  const [singular, plural] = PERIODO[groupBy];
  return `${n} ${n === 1 ? singular : plural}`;
}

function cell(value: number | null, isPercent: boolean): string {
  if (value === null) return "—";
  return isPercent ? `${pct.format(value)}%` : formatQuantity(value);
}

/**
 * O indicador do agrupamento como SELO, e não como mais um número na fileira.
 *
 * A grade é uma parede de números iguais; o aproveitamento é o único que carrega veredito, e um
 * veredito em texto puro some no meio dos outros oito. O selo é o que o gestor procura quando
 * corre o olho pela coluna.
 *
 * Sem meta declarada não há veredito — aí o valor volta a ser número, porque um selo colorido sem
 * régua afirmaria uma aprovação que ninguém definiu. E o abaixo-da-meta leva triângulo: cor
 * sozinha não é status (a mesma regra do "dado suspeito" logo acima).
 */
function IndicatorBadge({ value, target }: { value: number | null; target: number | null }) {
  if (value === null) return <span className="text-text-tertiary">—</span>;
  const label = `${pct.format(value)}%`;
  if (target === null) return <>{label}</>;

  const abaixo = value < target;
  return (
    <Pill
      tone={abaixo ? "warning" : "success"}
      icon={abaixo ? <AlertTriangle size={11} aria-hidden /> : undefined}
      className="text-[12px] font-semibold tabular-nums"
      title={`meta ${pct.format(target)}%`}
    >
      {label}
    </Pill>
  );
}

export function ClosingGrid({ stage, groupBy, mode, onModeChange, heroId }: Props) {
  const cols = closingColumns(stage, mode, heroId);
  const hero = stage.indicators.find((i) => i.id === heroId) ?? stage.indicators[0] ?? null;

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-baseline gap-2.5">
          <h3 className="text-[13px] font-medium text-text-primary">Fechamento</h3>
          <span className="text-[11px] text-text-tertiary">{contagem(stage, groupBy)}</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onModeChange(mode === "summary" ? "full" : "summary")}
        >
          {mode === "summary" ? "Todas as colunas" : "Resumir colunas"}
        </Button>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Período</TableHeaderCell>
              {cols.map((c) => (
                <TableHeaderCell key={c.key} className="text-right">
                  {c.label}
                </TableHeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {stage.buckets.map((b) => (
              <TableRow key={b.key}>
                <TableCell className="whitespace-nowrap">
                  {formatBucketLabel(b, groupBy)}
                  {b.partial ? (
                    <span className="ml-2 text-[11px] text-text-tertiary">parcial</span>
                  ) : null}
                  {/* Spec 084 — o agrupamento contém dia com base ausente ou zerada. Ícone E
                      palavra: um triângulo sozinho não diz o que houve. */}
                  {b.indicators.some((i) => i.suspicious) ? (
                    <span className="ml-2 inline-flex items-center gap-1 text-[11px] text-rd-warning-700">
                      <AlertTriangle size={11} aria-hidden />
                      dado suspeito
                    </span>
                  ) : null}
                </TableCell>
                {cols.map((c) => (
                  <TableCell key={c.key} className="text-right font-mono tabular-nums">
                    {c.key === "indicator" ? (
                      <IndicatorBadge value={c.value(b)} target={hero?.target ?? null} />
                    ) : (
                      cell(c.value(b), false)
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            <TableRow className="font-medium">
              <TableCell>Total do período</TableCell>
              {cols.map((c) => (
                <TableCell key={c.key} className="text-right font-mono tabular-nums">
                  {cell(
                    c.value({
                      ...stage,
                      key: "",
                      from: "",
                      to: "",
                      partial: false,
                      indicators: hero ? [hero] : [],
                    }),
                    c.key === "indicator"
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </section>
  );
}
